import React, { useState, useMemo, useEffect } from 'react';
import { GoogleMap, LoadScript, Marker, Polygon } from '@react-google-maps/api';
import { SublocalityDto } from '../../../api-client';
import { usePatientCoordinates } from '../../../stores/Address/address.queries';

interface GoogleMapsSublocalitiesProps {
  sublocalities: SublocalityDto[];
}

const GoogleMapsSublocalities: React.FC<GoogleMapsSublocalitiesProps> = ({ sublocalities }) => {
  const [center, setCenter] = useState<google.maps.LatLngLiteral>({ lat: 52.6167, lng: 10.05 });
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  
  // Fetch patient coordinates
  const { data: coordinates = [] } = usePatientCoordinates();
  
  // Set center when coordinates change
  useEffect(() => {
    if (coordinates.length > 0) {
      setCenter({
        lat: coordinates[0].addressLatitude,
        lng: coordinates[0].addressLongitude,
      });
    }
  }, [coordinates]);

  const mapStyles = {
    height: '500px',
    width: '100%'
  };

  return (
    <LoadScript 
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
      onLoad={() => setIsGoogleMapsLoaded(true)}
    >
      <GoogleMap
        mapContainerStyle={mapStyles}
        zoom={11}
        center={center}
      >
        {/* Patient Markers */}
        {useMemo(() => 
          coordinates.map((patient, index) => {
            // Create custom icon only if google maps is fully loaded and patient has color
            const customIcon = patient.sublocalityColor && isGoogleMapsLoaded ? {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: patient.sublocalityColor,
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            } : undefined;
            return (
            <Marker
              key={`patient-${index}`}
              position={{
              lat: patient.addressLatitude,
              lng: patient.addressLongitude,
              }}
              title={patient.sublocalityId ?? "Patient location"}
              icon={customIcon}
              />
            );
          }), [coordinates, isGoogleMapsLoaded])} {/* Only recreate markers if coordinates or isGoogleMapsLoaded changes */}

        {/* Sublocality Polygons */}
        {useMemo(() =>
          sublocalities.map((sublocality, index) => {
          const geometry = sublocality.geometry as any;
          
          if (geometry?.type === "Polygon" && geometry.coordinates) {
            // Convert GeoJSON [lng, lat] to Google Maps {lat, lng}
            const paths = geometry.coordinates[0].map((coord: [number, number]) => ({
              lat: coord[1],
              lng: coord[0]
            }));

            return (
              <Polygon
                key={`polygon-${sublocality.id || index}`}
                paths={paths}
                options={{
                  fillColor: sublocality.color || '#FF0000',
                  fillOpacity: 0.3,
                  strokeColor: sublocality.color || '#FF0000',
                  strokeOpacity: 1,
                  strokeWeight: 2,
                  zIndex: 2,
                  
                }}
              />
            );
          }
          return null;
        }), [sublocalities])} {/* Only recreate polygons if sublocalities changes */}

      </GoogleMap>
    </LoadScript>
  );
};

export default GoogleMapsSublocalities;
